import { createGenerateClassName, createMuiTheme, jssPreset, MuiThemeProvider } from "@material-ui/core/styles";
import React, { ReactElement, ReactNodeArray, useEffect, useState } from "react";
import { connect } from "react-redux";
import { breakpoints as appBreakpoints } from "src/styles/createBreakpoints";
import createStyledComponentsTheme from "src/styles/createStyledComponentsTheme";
import { loadFontFamily } from "src/utils/font";
import { StyleSheetManager, ThemeProvider as StyledComponentsThemeProvider } from "styled-components";
import { ApplicationStore } from "Types/state";
import { useFrame } from "react-frame-component";
import JssProvider from "react-jss/lib/JssProvider";
import { create } from "jss";
import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import AppGlobalStyles from "src/providers/theme/AppGlobalStyles";

// Turns off auto loading fontawesome css to prevent it from being loaded outside of the iframe context
// import "@fortawesome/fontawesome-svg-core/styles.css"; will ensure loading happens within the iframe
config.autoAddCss = false;

interface ThemeProviderComponentProps {
    children: ReactElement<any> | (ReactNodeArray & ReactElement<any>);
}

const ThemeProvider = ({
    children,
    fontNameFromSettings,
    settings,
}: ThemeProviderComponentProps & ReturnType<typeof mapStateToProps>) => {
    const iframe = useFrame();

    const [loadedFont, setLoadedFont] = useState("Roboto");

    // Load font effect.
    // Automatically fetches new font on name change.
    useEffect(() => {
        loadFontFamily(iframe.window, fontNameFromSettings)
            .then((fontFamily: string) => {
                setLoadedFont(fontFamily);
            })
            .catch((e) => console.error("Unable to load specified font. Keeping defaults.", e));
    }, [fontNameFromSettings, iframe.window]);

    const styledComponentsTheme = createStyledComponentsTheme(settings, loadedFont);

    const muiTheme = createMuiTheme({
        breakpoints: {
            values: appBreakpoints,
        },
        spacing: styledComponentsTheme.spacing,
        typography: {
            fontFamily: `${loadedFont}, sans-serif`,
            useNextVariants: true,
        },
        zIndex: {
            appBar: 1220101,
            drawer: 1220201,
            mobileStepper: 1220001,
            modal: 1220301,
            snackbar: 1220401,
            tooltip: 1220501,
        },
    });

    const jss = create({
        ...jssPreset(),
        createGenerateClassName,
        insertionPoint: iframe.document.getElementById("jss-insertion-point"),
    });

    return (
        <JssProvider jss={jss}>
            <MuiThemeProvider theme={muiTheme}>
                <StyleSheetManager target={iframe.document.head}>
                    <StyledComponentsThemeProvider theme={styledComponentsTheme}>
                        <AppGlobalStyles />
                        {children}
                    </StyledComponentsThemeProvider>
                </StyleSheetManager>
            </MuiThemeProvider>
        </JssProvider>
    );
};

const mapStateToProps = ({ account }: ApplicationStore) => ({
    fontNameFromSettings: account?.settings?.electric?.font_name ?? "Roboto",
    settings: account?.settings?.electric,
});

export default connect(mapStateToProps)(ThemeProvider);
